import {
  Box,
  Card,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { P, match } from 'ts-pattern';
import TablerIconChevronRight from '~icons/tabler/chevron-right';
import { cn } from '#/utils/styles';
import type {
  ChatMessagePartToolItem,
  ChatMessagePartToolStatus,
} from './types';

export interface ChatMessagePartToolContainerProps {
  /**
   * The Icon to show
   */
  icon: React.ReactNode;
  /**
   * The class name to apply to the component
   */
  className?: string;
  /**
   * The default open state of the tool if its a collapsible tool
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Whether to disable the card
   * @default true
   */
  card?: boolean;
  /**
   * Whether to render the tool in compact mode
   * @default false
   */
  compact?: boolean;
  /**
   * The title of section
   */
  title: React.ReactNode;
  /**
   * The items to render (if children are provided, the items will not be rendered)
   */
  items?: ChatMessagePartToolItem[];
  /**
   * The children to render (if children are provided, the items will not be rendered)
   */
  children?: React.ReactNode;
  /**
   * The status of the tool
   */
  status: ChatMessagePartToolStatus;
  /**
   * The function to call when the tool is toggled
   */
  onToggle?: ({ prev, next }: { prev: boolean; next: boolean }) => void;
}

function ChatMessagePartToolContainer({
  icon,
  card = true,
  defaultOpen = false,
  compact = false,
  title,
  items = [],
  children = null,
  status,
  onToggle,
  className,
}: ChatMessagePartToolContainerProps) {
  const [open, setOpen] = React.useState(defaultOpen);

  const handleToggle = React.useCallback(() => {
    const next = !open;
    setOpen(next);
    onToggle?.({ prev: open, next });
  }, [onToggle, open]);

  const Container = React.useMemo(() => {
    if (card) {
      return Card;
    }
    return 'div';
  }, [card]);

  return (
    <Box
      component={Container}
      className={cn(
        'group relative bg-background-default border-divider hover:border-divider/50',
        className
      )}
    >
      {!compact && (
        <LinearProgress
          className={cn('absolute -bottom-0.5 right-0 left-0 w-full', {
            hidden: status !== 'loading',
          })}
        />
      )}
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        className={cn({
          'hover:opacity-80 cursor-pointer':
            (items.length > 0 || children) && status === 'result',
          'text-sm py-1 px-2': compact,
        })}
        onClick={handleToggle}
      >
        <div
          className={cn('size-8 flex items-center justify-center', {
            hidden: compact,
          })}
        >
          {(items.length > 0 || children) && (
            <span
              className={cn({
                'hidden h-fit w-fit group-hover:flex':
                  !!icon && status === 'result',
                flex: !icon && status === 'result',
              })}
            >
              <TablerIconChevronRight
                className={cn({
                  'rotate-90': open,
                })}
              />
            </span>
          )}
          <span
            className={cn('flex h-fit w-fit', {
              'group-hover:hidden': status === 'result',
            })}
          >
            {icon}
          </span>
        </div>
        <div className='w-full'>{title}</div>
      </Stack>
      {open && children}
      {open && items.length > 0 && (
        <div className='max-h-[480px] overflow-y-auto'>
          <List dense disablePadding>
            {items.map((item) => (
              <ListItem
                key={item.title.toString()}
                className={cn(compact && 'py-0.5 px-1')}
                dense
                disablePadding
              >
                <ListItemButton
                  onClick={item.onClick}
                  component={match(item)
                    .with({ href: P.string }, () => 'a' as const)
                    .with({ to: P.string }, () => RouterLink)
                    .otherwise(() => 'div' as const)}
                  to={item.to}
                  href={item.href}
                  target={item.href ? '_blank' : undefined}
                  rel={item.href ? 'noopener noreferrer' : undefined}
                >
                  {item.icon && !compact && (
                    <ListItemIcon className='min-w-[24px]'>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    className={cn({
                      'm-0': compact,
                    })}
                    primary={item.title}
                    slotProps={{
                      primary: {
                        className: cn(
                          'text-ellipsis text-nowrap overflow-hidden',
                          {
                            'text-sm': compact,
                          }
                        ),
                      },
                    }}
                  />
                  <div className='flex-1 min-h-1 min-w-2' />
                  {match(item)
                    .with(
                      {
                        collapsible: true,
                        collapsibleContent: P.not(P.nullish),
                      },
                      (x) => <div className='size-fit'>open</div>
                    )
                    .with({ action: P.not(P.nullish) }, (x) => (
                      <div className='size-fit'>{x.action}</div>
                    ))
                    .otherwise(() => null)}
                </ListItemButton>
                {item.collapsible && item.collapsibleContent && (
                  <div className='w-full flex-wrap'>
                    {item.collapsibleContent}
                  </div>
                )}
              </ListItem>
            ))}
          </List>
        </div>
      )}
    </Box>
  );
}

export default React.memo(ChatMessagePartToolContainer);
